import { v4 as uuidv4 } from "uuid";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "../postCall/bearerToken";

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getBearerToken();
  return {
    Authorization: `Bearer ${token}`,
  };
};

const createRequestOptions = (header?: Record<string, string>): RequestInit => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  if (header) {
    for (const key in header) {
      headers.append(key, header[key]);
    }
  }

  const options: RequestInit = {
    method: "POST",
    headers,
    redirect: "follow",
  };

  return options;
};

const handleApiResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return await response.json();
};

export interface FilenameSummaryItem {
  filename: string;
  unique_patient_mrn_count?: number;
  row_count: number;
  resubmit_count: number;
  pending_count: number;
  dumped_at: string;
}

export interface FilenameSummaryApiResponse {
  success: boolean;
  message: string;
  items: FilenameSummaryItem[];
  page: number;
  page_size: number;
  total_pages: number;
  total_count: number;
}

export interface OrderByItem {
  column: string;
  direction: "ASC" | "DESC";
}

/**
 * Server-side preset filter.
 * "sftp_push_pending" keeps only filenames holding at least one row with
 * sftp_status='resubmit' and a terminal response_status, and narrows
 * resubmit_count to those rows. Omit for the unfiltered list.
 */
export type FilenameSummaryView = "default" | "sftp_push_pending";

export interface FetchFilenameSummaryParams {
  page?: number;
  page_size?: number;
  org_id?: string;
  view?: FilenameSummaryView;
  order_by?: OrderByItem[];
}

export const fetchFilenameSummary = async (
  params?: FetchFilenameSummaryParams,
): Promise<FilenameSummaryApiResponse> => {
  // Undefined keys are dropped by JSON.stringify, so callers that omit `view`
  // send exactly the payload they sent before the filter existed.
  const body = {
    page: params?.page,
    page_size: params?.page_size,
    org_id: params?.org_id,
    request_id: uuidv4(),
    view: params?.view,
    order_by: params?.order_by,
  };
  const headers = await getAuthHeaders();
  const requestOptions = createRequestOptions(headers);
  requestOptions.body = JSON.stringify(body);

  try {
    const response = await fetch(
      API_ENDPOINTS.GET_FILENAME_SUMMARY,
      requestOptions,
    );
    const result = await handleApiResponse(response);
    return result;
  } catch (error) {
    logError(error as Error, "Error in fetchFilenameSummary");
    throw error;
  }
};
